import { useQuery } from '@tanstack/react-query';
import { collection, query, where, getCountFromServer } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { COLLECTIONS } from '../../config/collections';
import { qboRead } from '../../lib/qbo/qboReadClient';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useIsExternal } from '../useIsExternal';

/**
 * Lightweight hook that returns the number of unlinked QBO invoices.
 * Used to conditionally show the "Invoice Matching" nav item.
 *
 * Compares total project_invoice_links count against a rough QBO invoice count.
 * Only enabled when the caller passes `enabled: true` (i.e., when in Projects module).
 */
export function useUnlinkedInvoiceCount(enabled: boolean): number {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId || '';
  const isExternal = useIsExternal();

  const { data = -1 } = useQuery({
    queryKey: ['unlinked-invoice-count', orgId],
    queryFn: async (): Promise<number> => {
      if (!orgId) return 0;

      // Count how many invoices have been linked or dismissed
      const linksRef = collection(db, COLLECTIONS.PROJECT_INVOICE_LINKS);
      const linksQuery = query(linksRef, where('organizationId', '==', orgId));
      const linksSnap = await getCountFromServer(linksQuery);
      const handledCount = linksSnap.data().count;

      // Count total QBO invoices (rough proxy — not all are matchable,
      // but if handled >= total, there are definitely no unlinked ones)
      // projects_qboRead.invoiceCount (#6381): the org's own invoices only (the old count was the whole collection)
      const { count: totalCount } = await qboRead<{ count: number }>('projects_qboRead', { op: 'invoiceCount' });

      return Math.max(0, totalCount - handledCount);
    },
    enabled: enabled && !!orgId && !isExternal,
    staleTime: 1000 * 60 * 5, // 5 min — don't refetch often
  });

  // External users are refused these reads (P2 read ceiling): report 0 so the
  // "Invoice Matching" nav item is hidden rather than shown as "loading".
  if (isExternal) return 0;
  // -1 = still loading, treat as "show the nav item"
  return data;
}
